<template>
  <property-report-card
    title="Property Rankings"
    description="Compare asking and effective rents by bedroom count across your market."
    :printing-mode="printingMode"
    :is-empty="!unitsFound(filteredProperties)"
  >
    <template #actions>
      <q-select
        v-model="metric"
        transition-duration="100"
        :options="metricOptions"
        label="Metric"
        dense
        outlined
        style="width: 180px"
        @update:model-value="
          (value) => $tracker.changedFilter('metric', 'property_rankings', value.label)
        "
      />
    </template>
    <div class="row q-col-gutter-sm q-pa-sm items-stretch">
      <div
        v-for="(properties, bed) in unitTypeMetric"
        :key="bed"
        class="col"
        :class="{
          'col-3': unitTypeMetricLength >= 5 && $q.screen.lt.lg,
          'col-6': unitTypeMetricLength === 1 || $q.screen.lt.lg,
          'col-12': $q.screen.lt.md
        }"
      >
        <q-card flat bordered class="full-height">
          <q-card-section
            class="row justify-between items-center"
            :style="{
              borderBottom: `4px solid ${unitTypeColor(bed)}`
            }"
          >
            <div class="text-h6">{{ unitTypeName(bed) }}</div>
          </q-card-section>
          <q-card-section class="row q-col-gutter-x-md q-col-gutter-y-xs">
            <div v-for="propertyData in properties" :key="propertyData.label" class="col-12">
              <div class="row items-center q-gutter-xs">
                <div
                  v-if="!propertyData.isAverage"
                  class="text-caption col-4 text-right ellipsis"
                  :class="
                    propertyData.id === subjectDetail?.id
                      ? 'text-bold'
                      : $q.dark.isActive
                        ? 'text-grey-5'
                        : 'text-grey-9'
                  "
                >
                  {{ propertyData.label }}
                </div>
                <div class="col">
                  <div
                    v-if="!propertyData.isAverage && propertyData.barPerc"
                    :style="{
                      height: '8px',
                      width: `${propertyData.barPerc * 100}%`,
                      borderRadius: '4px',
                      minWidth: '2px',
                      backgroundColor:
                        propertyData.id === subjectDetail?.id
                          ? 'var(--q-primary)'
                          : `${unitTypeColor(bed)}80`
                    }"
                  ></div>
                  <div v-else-if="propertyData.isAverage" class="row items-center q-mr-xs">
                    <div
                      :style="{
                        borderTop: '2px dashed #61616182',
                        flex: '0.3',
                        marginRight: '8px'
                      }"
                    ></div>
                    <div
                      class="text-caption"
                      :class="$q.dark.isActive ? 'text-grey-5' : 'text-grey-9'"
                      style="white-space: nowrap"
                    >
                      {{ propertyData.label }}
                      <q-icon v-if="subjectDetail" name="info_outline">
                        <q-tooltip>
                          Computed with underlying units (Does not include
                          {{ subjectDetail.building_name || subjectDetail.street_address }})
                        </q-tooltip>
                      </q-icon>
                    </div>
                    <div
                      :style="{
                        height: '2px',
                        borderTop: '2px dashed #61616182',
                        flex: '1',
                        marginLeft: '8px'
                      }"
                    ></div>
                  </div>
                </div>
                <div
                  v-if="propertyData.value"
                  class="text-weight-medium col-2"
                  :class="{
                    'text-bold': propertyData.id === subjectDetail?.id,
                    'text-grey-5': propertyData.id !== subjectDetail?.id && $q.dark.isActive,
                    'text-grey-9': propertyData.id !== subjectDetail?.id && !$q.dark.isActive
                  }"
                >
                  ${{
                    metric.value.includes('psf')
                      ? formatNumber(propertyData.value, false, 2)
                      : formatNumber(propertyData.value)
                  }}
                </div>
                <div v-else class="col-2 row justify-center">
                  <q-icon name="warning" />
                  <q-tooltip> Missing data, such as square feet, needed for calculation </q-tooltip>
                </div>
              </div>
            </div>
          </q-card-section>
        </q-card>
      </div>
    </div>
  </property-report-card>
</template>

<script lang="ts">
import {
  type Availability,
  type PropertyDetailsResponse,
  type Price,
  type AvailabilityPeriod,
  avg
} from 'common'
import { formatMoney, formatNumber, unitsFound, unitTypeColor } from '@/utils'
import PropertyReportCard from '@/components/PropertyReportCard.vue'
import type { PropType } from 'vue'

interface PropertyRankingItem {
  id: string
  label: string
  value: number | null
  unitCount: number
  barPerc: number
  isAverage?: boolean
}

interface BedRankings {
  [key: string]: PropertyRankingItem[]
}

export default {
  name: 'PropertyCompareRankingsChart',
  components: { PropertyReportCard },
  props: {
    subjectDetail: {
      type: Object as PropType<PropertyDetailsResponse | null>,
      default: null
    },
    filteredProperties: {
      type: Array<{
        prop: PropertyDetailsResponse
        units: {
          unit: Availability
          lastHistory: Price
          period: AvailabilityPeriod
        }[]
      }>,
      required: true
    },
    printingMode: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      unitTypeColor,
      formatMoney,
      formatNumber,
      metric: { label: 'Asking Rent', value: 'asking' },
      metricOptions: [
        { label: 'Asking Rent', value: 'asking' },
        { label: 'Effective Rent', value: 'effective' },
        { label: 'Asking PSF', value: 'asking_psf' },
        { label: 'Effective PSF', value: 'effective_psf' }
      ]
    }
  },
  computed: {
    unitTypeMetric(): BedRankings {
      // put every unit from every property into the correct unit type bucket
      const unitTypeGroups: Record<
        string,
        Record<
          string,
          {
            prop: PropertyDetailsResponse
            units: {
              unit: Availability
              lastHistory: Price
              period: AvailabilityPeriod
            }[]
          }
        >
      > = this.filteredProperties.reduce(
        (acc, currentProperty) => {
          const { prop, units } = currentProperty
          units.forEach((u) => {
            const key = u.unit.bed
            if (key === null) {
              return acc
            }
            if (!(key in acc)) {
              acc[key] = { [prop.id]: { prop: prop, units: [] } }
            }
            if (!(prop.id in acc[key])) {
              acc[key][prop.id] = { prop: prop, units: [] }
            }
            acc[key][prop.id].units.push({
              ...u
            })
          })
          return acc
        },
        {} as Record<
          string,
          Record<
            string,
            {
              prop: PropertyDetailsResponse
              units: {
                unit: Availability
                lastHistory: Price
                period: AvailabilityPeriod
              }[]
            }
          >
        >
      )

      // calculate average price, sort, and calculate bar percentages in one pass
      const data: BedRankings = {}
      Object.keys(unitTypeGroups).forEach((unitType) => {
        data[unitType] = [] as PropertyRankingItem[]

        // Calculate average prices for each property
        Object.keys(unitTypeGroups[unitType]).forEach((property) => {
          const { prop, units } = unitTypeGroups[unitType][property]
          const values = units.reduce((acc, curr) => {
            let price = null
            const sqft = curr.unit.sqft

            // Skip if no square footage available for PSF calculations
            if (
              (this.metric.value === 'asking_psf' || this.metric.value === 'effective_psf') &&
              !sqft
            ) {
              return acc
            }

            if (this.metric.value === 'asking') {
              price = curr.lastHistory.price || curr.lastHistory.min_price
            } else if (this.metric.value === 'effective') {
              price =
                curr.lastHistory.effective_price ||
                curr.lastHistory.min_effective_price ||
                curr.lastHistory.price ||
                curr.lastHistory.min_price
            } else if (this.metric.value === 'asking_psf') {
              const askingPrice = curr.lastHistory.price || curr.lastHistory.min_price
              if (!sqft) {
                price = null
              } else {
                price = askingPrice ? askingPrice / sqft : null
              }
            } else if (this.metric.value === 'effective_psf') {
              const effectivePrice =
                curr.lastHistory.effective_price ||
                curr.lastHistory.min_effective_price ||
                curr.lastHistory.price ||
                curr.lastHistory.min_price
              if (!sqft) {
                price = null
              } else {
                price = effectivePrice ? effectivePrice / sqft : null
              }
            }

            if (price !== null) {
              acc.push(price)
            }
            return acc
          }, [] as number[])

          data[unitType].push({
            id: prop.id,
            label: prop.building_name ?? prop.street_address,
            value: avg(values),
            unitCount: values.length,
            barPerc: 0 // will be calculated after sorting
          })
        })

        // Calculate average and add it to the data (only if more than 1 property)
        if (data[unitType].length > 1) {
          // Exclude the subject property from the average calculation
          const comparableValues = data[unitType]
            .filter((item) => item.id !== this.subjectDetail?.id)
            .flatMap((item) =>
              item.value === null ? [] : Array.from({ length: item.unitCount }, () => item.value)
            )

          // Add average as a special entry
          data[unitType].push({
            id: 'average',
            label: 'Comp Average',
            value: avg(comparableValues),
            unitCount: comparableValues.length,
            barPerc: 0,
            isAverage: true
          })

          // Sort to include the average in proper position
          data[unitType].sort((a, b) => (b.value ?? 0) - (a.value ?? 0))
        }

        // Calculate bar percentages with highest value being 1 (100%)
        if (data[unitType].length > 0) {
          const maxValue = data[unitType][0].value
          if (maxValue && maxValue > 0) {
            data[unitType].forEach((item) => {
              item.barPerc = item.value ? item.value / maxValue : 0
            })
          }
        }
      })

      return data
    },
    unitTypeMetricLength() {
      return Object.keys(this.unitTypeMetric).length
    }
  },
  methods: {
    unitsFound,
    avg,
    unitTypeName(bed: string): string {
      return bed === '0' ? 'Studio' : `${bed} BRs`
    }
  }
}
</script>

<style scoped lang="scss">
.full-height {
  height: 100%;
}
</style>
